/**
 * Serialization utilities for rich input segments
 * Converts InputSegment[] to RichMessagePayload for the API
 */

import type {
  InputSegment,
  MentionEntity,
  RichMessagePayload,
} from '@/types/rich-input'

/**
 * Serialize input segments into a payload suitable for the API.
 *
 * Mentions are encoded inline as `@[Display Name](entityType:id)` so the
 * backend / LLM still receives a readable string, while the structured
 * `mentions` array carries the typed data in `contextData`.
 */
export function serializeSegments(
  segments: readonly InputSegment[],
  command?: string
): RichMessagePayload {
  const mentions: MentionEntity[] = []
  let content = ''

  for (const segment of segments) {
    if (segment.type === 'text') {
      content += segment.text
    } else {
      const { entity } = segment
      content += `@[${entity.displayName}](${entity.entityType}:${entity.id})`
      mentions.push(entity)
    }
  }

  return {
    content: content.trim(),
    mentions,
    ...(command ? { command } : {}),
  }
}

/**
 * Extract plain-text content from segments (no mention markup).
 * Used for character count validation.
 */
export function getPlainText(segments: readonly InputSegment[]): string {
  return segments
    .map((segment) =>
      segment.type === 'text' ? segment.text : `@${segment.entity.displayName}`
    )
    .join('')
}

/**
 * Build segments from plain text (no mentions).
 * Convenience for converting a raw string into the segment model.
 */
export function textToSegments(text: string): readonly InputSegment[] {
  if (!text) {
    return []
  }
  return [{ type: 'text', text }]
}

/**
 * Check whether the segments contain any meaningful content.
 */
export function isSegmentsEmpty(segments: readonly InputSegment[]): boolean {
  return getPlainText(segments).trim().length === 0
}
